import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, CheckCircle2, XCircle, Upload, FileText, Download, Trash2, Pencil, Filter } from "lucide-react";

const EMPTY = {
  entity_type: "tank",
  entity_id: "",
  batch: "",
  density: 0.832,
  temperature: 28.5,
  sulphur_ppm: 8.0,
  viscosity_cst: 3.0,
  water_test_pass: true,
  appearance_ok: true,
  test_type: "inhouse",
  lab_name: "",
  lab_report_url: "",
  remarks: "",
};

export default function Quality() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = ["admin", "accounts", "manager"].includes(user?.role);

  const [filterType, setFilterType] = useState("all");
  const [filterVerdict, setFilterVerdict] = useState("all");

  const { data } = useQuery({
    queryKey: ["quality", filterType, filterVerdict],
    queryFn: () =>
      api
        .get("/quality/tests", {
          params: {
            test_type: filterType === "all" ? "" : filterType,
            verdict: filterVerdict === "all" ? "" : filterVerdict,
          },
        })
        .then((r) => r.data),
  });
  const { data: tanks } = useQuery({ queryKey: ["quality-tanks"], queryFn: () => api.get("/inventory/tanks").then((r) => r.data) });
  const { data: bowsers } = useQuery({ queryKey: ["quality-bowsers"], queryFn: () => api.get("/fleet/bowsers").then((r) => r.data) });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      entity_type: t.entity_type || "tank",
      entity_id: t.entity_id || "",
      batch: t.batch || "",
      density: t.density || 0,
      temperature: t.temperature || 0,
      sulphur_ppm: t.sulphur_ppm || 0,
      viscosity_cst: t.viscosity_cst || 0,
      water_test_pass: t.water_test_pass !== false,
      appearance_ok: t.appearance_ok !== false,
      test_type: t.test_type || "inhouse",
      lab_name: t.lab_name || "",
      lab_report_url: t.lab_report_url || "",
      remarks: t.remarks || "",
    });
    setOpen(true);
  };

  const create = useMutation({
    mutationFn: (p) => api.post("/quality/tests", p),
    onSuccess: (r) => {
      toast.success(`Test recorded · ${r.data.pass_fail}`);
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["quality"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/quality/tests/${id}`, patch),
    onSuccess: (r) => {
      toast.success(`Updated · ${r.data.pass_fail}`);
      setOpen(false);
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["quality"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/quality/tests/${id}`),
    onSuccess: () => {
      toast.success("Test deleted");
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["quality"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  const entities = form.entity_type === "tank" ? (tanks?.items || []) : (bowsers?.items || []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/quality/upload-report", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF("lab_report_url", r.data.url);
      toast.success(`Uploaded ${r.data.filename} (${r.data.size_kb} KB)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const submitForm = () => {
    const payload = { ...form };
    if (payload.test_type !== "lab") {
      payload.lab_name = "";
    }
    if (editing) update.mutate({ id: editing.id, patch: payload });
    else create.mutate(payload);
  };

  const downloadPdf = async (t) => {
    try {
      const r = await api.get(`/exports/quality/${t.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quality_${t.code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  return (
    <div className="space-y-4" data-testid="quality-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Fuel Quality Tests</h1>
          <p className="text-sm text-muted-foreground">
            BS-VI HSD spec: Density 0.820–0.845 kg/L · Sulphur ≤ 10 ppm · Viscosity 2.0–4.5 cSt @40°C · Water &amp; Appearance pass.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Filter size={12} /> Filter:
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-9 w-36" data-testid="qt-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All test types</SelectItem>
              <SelectItem value="inhouse">In-house only</SelectItem>
              <SelectItem value="lab">Lab only</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterVerdict} onValueChange={setFilterVerdict}>
            <SelectTrigger className="h-9 w-32" data-testid="qt-filter-verdict"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All verdicts</SelectItem>
              <SelectItem value="PASS">Pass only</SelectItem>
              <SelectItem value="FAIL">Fail only</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} data-testid="btn-new-quality">
            <Plus size={14} className="mr-1" /> Record test
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-3 py-3">Code</th>
                <th className="px-3 py-3">Tested</th>
                <th className="px-3 py-3">Entity</th>
                <th className="px-3 py-3">Batch</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3 text-right">Density</th>
                <th className="px-3 py-3 text-right">Sulphur</th>
                <th className="px-3 py-3 text-right">Viscosity</th>
                <th className="px-3 py-3 text-right">Temp</th>
                <th className="px-3 py-3">Water</th>
                <th className="px-3 py-3">Appear.</th>
                <th className="px-3 py-3">Verdict</th>
                <th className="px-3 py-3">Report</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((t) => (
                <tr key={t.id} className="border-b border-border/40 text-sm hover:bg-muted/30" data-testid={`qt-row-${t.code}`}>
                  <td className="px-3 py-3 font-mono font-semibold">{t.code}</td>
                  <td className="px-3 py-3 text-xs">{t.tested_at ? new Date(t.tested_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-3 text-xs">
                    <div className="font-semibold uppercase">{t.entity_type}</div>
                    <div className="text-muted-foreground">{t.entity_id?.slice(0, 8)}</div>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">{t.batch || "—"}</td>
                  <td className="px-3 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${t.test_type === "lab" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"}`}>
                      {t.test_type || "inhouse"}
                    </span>
                    {t.test_type === "lab" && t.lab_name && (
                      <div className="mt-0.5 text-[10px] text-muted-foreground">{t.lab_name}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{t.density || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{t.sulphur_ppm ? `${t.sulphur_ppm} ppm` : "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{t.viscosity_cst ? `${t.viscosity_cst} cSt` : "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs">{t.temperature ? `${t.temperature}°` : "—"}</td>
                  <td className="px-3 py-3">
                    {t.water_test_pass
                      ? <CheckCircle2 className="text-emerald-600" size={16} />
                      : <XCircle className="text-red-600" size={16} />}
                  </td>
                  <td className="px-3 py-3">
                    {t.appearance_ok
                      ? <CheckCircle2 className="text-emerald-600" size={16} />
                      : <XCircle className="text-red-600" size={16} />}
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                        t.pass_fail === "FAIL"
                          ? "bg-red-100 text-red-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                      title={(t.fail_reasons || []).join("; ")}
                    >
                      {t.pass_fail || "PASS"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    {t.lab_report_url ? (
                      <a
                        href={t.lab_report_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        data-testid={`qt-lab-${t.code}`}
                      >
                        <FileText size={12} /> View
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => downloadPdf(t)}
                        className="rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                        title="Download PDF report"
                        data-testid={`qt-pdf-${t.code}`}
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => openEdit(t)}
                        className="rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                        title="Edit"
                        data-testid={`qt-edit-${t.code}`}
                      >
                        <Pencil size={14} />
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => setConfirmDelete(t)}
                          className="rounded p-1 text-muted-foreground hover:bg-red-100 hover:text-red-600"
                          title="Delete"
                          data-testid={`qt-del-${t.code}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(!data?.items || data.items.length === 0) && (
                <tr>
                  <td colSpan={14} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No tests match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setEditing(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit test · ${editing.code}` : "Record quality test"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="qt-form">
            <div>
              <Label>Entity type</Label>
              <Select value={form.entity_type} onValueChange={(v) => setF("entity_type", v)}>
                <SelectTrigger data-testid="qt-entity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tank">Tank</SelectItem>
                  <SelectItem value="bowser">Bowser</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>{form.entity_type === "tank" ? "Tank" : "Bowser"}</Label>
              <Select value={form.entity_id} onValueChange={(v) => setF("entity_id", v)}>
                <SelectTrigger data-testid="qt-target"><SelectValue placeholder="Pick one" /></SelectTrigger>
                <SelectContent>
                  {entities.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.code} · {e.name || e.registration_no}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Batch</Label>
              <Input value={form.batch} onChange={(e) => setF("batch", e.target.value)} placeholder="BS-VI-2026-…" />
            </div>
            <div>
              <Label>Test type</Label>
              <Select value={form.test_type} onValueChange={(v) => setF("test_type", v)}>
                <SelectTrigger data-testid="qt-test-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inhouse">In-house</SelectItem>
                  <SelectItem value="lab">External lab</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              {form.test_type === "lab" ? (
                <>
                  <Label>Lab name</Label>
                  <Input
                    value={form.lab_name}
                    onChange={(e) => setF("lab_name", e.target.value)}
                    placeholder="e.g. SGS India / Intertek"
                    data-testid="qt-lab-name"
                  />
                </>
              ) : (
                <div />
              )}
            </div>

            <div>
              <Label>Density (kg/L)</Label>
              <Input
                type="number"
                step="0.001"
                value={form.density}
                onChange={(e) => setF("density", Number(e.target.value))}
                data-testid="qt-density"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">Spec 0.820 – 0.845</p>
            </div>
            <div>
              <Label>Sulphur (ppm)</Label>
              <Input
                type="number"
                step="0.1"
                value={form.sulphur_ppm}
                onChange={(e) => setF("sulphur_ppm", Number(e.target.value))}
                data-testid="qt-sulphur"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">BS-VI max 10 ppm</p>
            </div>
            <div>
              <Label>Viscosity @40°C (cSt)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.viscosity_cst}
                onChange={(e) => setF("viscosity_cst", Number(e.target.value))}
                data-testid="qt-viscosity"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">Spec 2.0 – 4.5</p>
            </div>
            <div>
              <Label>Temperature (°C)</Label>
              <Input
                type="number"
                step="0.1"
                value={form.temperature}
                onChange={(e) => setF("temperature", Number(e.target.value))}
              />
            </div>

            <div className="md:col-span-3 flex flex-wrap gap-3">
              <div className="flex items-center gap-3 rounded border p-2">
                <Switch checked={form.water_test_pass} onCheckedChange={(v) => setF("water_test_pass", v)} data-testid="qt-water" />
                <Label>Water test PASS</Label>
              </div>
              <div className="flex items-center gap-3 rounded border p-2">
                <Switch checked={form.appearance_ok} onCheckedChange={(v) => setF("appearance_ok", v)} data-testid="qt-appearance" />
                <Label>Appearance OK (clear & bright)</Label>
              </div>
            </div>

            {form.test_type === "lab" && (
              <div className="md:col-span-3">
                <Label>Lab report file</Label>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    ref={fileInput}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={onUpload}
                    className="hidden"
                    data-testid="qt-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInput.current?.click()}
                    disabled={uploading}
                    data-testid="qt-upload-btn"
                  >
                    <Upload size={14} className="mr-1.5" />
                    {uploading ? "Uploading…" : form.lab_report_url ? "Replace file" : "Upload lab report (PDF/JPG/PNG)"}
                  </Button>
                  {form.lab_report_url && (
                    <a
                      href={form.lab_report_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <FileText size={12} /> View uploaded file
                    </a>
                  )}
                </div>
                <p className="mt-1 text-[10px] text-muted-foreground">Max 10 MB. PDF preferred.</p>
              </div>
            )}

            <div className="md:col-span-3">
              <Label>Remarks / observations</Label>
              <Textarea rows={2} value={form.remarks} onChange={(e) => setF("remarks", e.target.value)} data-testid="qt-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
            <Button
              onClick={submitForm}
              disabled={!form.entity_id || create.isPending || update.isPending}
              data-testid="btn-save-quality"
            >
              {editing ? "Save changes" : "Save test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete quality test?</AlertDialogTitle>
            <AlertDialogDescription>
              Test <b>{confirmDelete?.code}</b> will be permanently removed. If a lab report was uploaded, that file will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="qt-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
